import type { RootState } from '/business/shared/state/store';
import { createSelector } from 'reselect';

import normalisePath from '/business/shared/functions/normalisePath';
import type { QuestionTreeItem } from '/business/shared/functions/types';
import type { BasketItem } from '/business/shared/state/reducers/basket';
import type { Step1SelectorProps } from '/business/shared/state/selectors/types';

import getAnswersSorted from './getAnswersSorted';
import getQuestionsAndSubQuestions from './getQuestionsAndSubQuestionsSelector';

export type QuestionWithAnswers = Omit<QuestionTreeItem, 'children' | 'groups' | 'questions' | 'subQuestions'> & {
  answers: BasketItem[];
  children?: QuestionWithAnswers[];
  groups?: QuestionWithAnswers[];
  questions?: QuestionWithAnswers[];
  subQuestions?: QuestionWithAnswers[];
};

const mergeAnswers = (qs: QuestionTreeItem[], as: BasketItem[]): QuestionWithAnswers[] =>
  qs.map((q) => {
    const { children, groups, questions, subQuestions, ...question } = q;
    return {
      ...question,
      answers: as.filter((a) => normalisePath({ answer: a }) === normalisePath({ question: q })),
      ...(!children ? {} : { children: mergeAnswers(children, as) }),
      ...(!groups ? {} : { groups: mergeAnswers(groups, as) }),
      ...(!questions ? {} : { questions: mergeAnswers(questions, as) }),
      ...(!subQuestions ? {} : { subQuestions: mergeAnswers(subQuestions, as) }),
    };
  });

type QuestionsWithAnswersSelector = (state: RootState, ownProps: Step1SelectorProps) => QuestionWithAnswers[];

const getQuestionsWithAnswersSelector = createSelector(
  [getQuestionsAndSubQuestions, getAnswersSorted],
  (questions, answers) => {
    if (!questions) {
      return [];
    }
    return mergeAnswers(questions, answers);
  },
) as QuestionsWithAnswersSelector;

export default function getQuestionsWithAnswers(state: RootState, ownProps: Step1SelectorProps): QuestionWithAnswers[] {
  return getQuestionsWithAnswersSelector(state, ownProps);
}
